import { useState, useCallback } from 'react';
import { message } from 'antd';
import { m207Api, FM210, FM211, FM212 } from '../../../services/m207Api';
import { Mode } from './useM207Mode';

export function useM207Record() {
  const [record, setRecord] = useState<FM210 | null>(null);
  const [formData, setFormData] = useState<Partial<FM210>>({});
  const [details, setDetails] = useState<Partial<FM211>[]>([]);
  const [loading, setLoading] = useState(false);

  /** Load a single record by ID */
  const loadRecord = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const data = await m207Api.get(id);
      setRecord(data);
      setFormData({ ...data });
      setDetails(data.details || []);
    } catch {
      message.error('載入失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single master field */
  const setField = useCallback((key: keyof FM210, value: FM210[keyof FM210]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single master field value */
  const getField = useCallback((key: keyof FM210): FM210[keyof FM210] => {
    return (formData as Record<string, FM210[keyof FM210]>)[key] ?? '';
  }, [formData]);

  /** Save (create or update) */
  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: FM210) => void,
  ) => {
    setLoading(true);
    try {
      const req = {
        master: formData as FM210,
        details: details as FM211[],
      };
      if (mode === 'add') {
        const result = await m207Api.create(req);
        message.success(`新增成功: ${result.fm210_01}`);
        onSuccess(result);
      } else {
        const result = await m207Api.update(formData.fm210_01!, req);
        message.success('修改成功');
        onSuccess(result);
      }
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : '儲存失敗';
      message.error(errMsg);
    } finally {
      setLoading(false);
    }
  }, [formData, details]);

  /** Delete current record */
  const handleDelete = useCallback(async (onSuccess: () => void) => {
    if (!record?.fm210_01) return;
    setLoading(true);
    try {
      await m207Api.delete(record.fm210_01);
      message.success('刪除成功');
      onSuccess();
    } catch {
      message.error('刪除失敗');
    } finally {
      setLoading(false);
    }
  }, [record]);

  /** Revert form data to last loaded record */
  const revertToRecord = useCallback(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details || []);
    }
  }, [record]);

  /** Reset form for add mode */
  const resetForAdd = useCallback(() => {
    setRecord(null);
    setFormData({ fm210_80: 'N' });
    setDetails([]);
  }, []);

  /** Apply result after save */
  const applyResult = useCallback((result: FM210) => {
    setRecord(result);
    setFormData({ ...result });
    setDetails(result.details || []);
  }, []);

  /** Add a detail row */
  const addDetailRow = useCallback(() => {
    setDetails(prev => [
      ...prev,
      {
        fm211_02: prev.length + 1,
        fm211_11: '', fm211_121: '', fm211_122: '',
        fm211_13: 0, fm211_14: '',
        board_numbers: [],
      },
    ]);
  }, []);

  /** Delete a detail row by index */
  const deleteDetailRow = useCallback((idx: number) => {
    setDetails(prev => prev.filter((_, i) => i !== idx));
  }, []);

  /** Update board numbers for a specific detail row */
  const updateBoardNumbers = useCallback((detailIdx: number, boardNumbers: Partial<FM212>[]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[detailIdx]) {
        next[detailIdx] = {
          ...next[detailIdx],
          board_numbers: boardNumbers as FM212[],
          fm211_13: boardNumbers.length,
        };
      }
      return next;
    });
  }, []);

  return {
    record, formData, details, loading,
    setDetails,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    addDetailRow, deleteDetailRow, updateBoardNumbers,
  };
}
